<template>
    <div>
        <div class="w-100">
            <div class="screen-content">
                <error-list></error-list>

                <div
                    v-if="booking"
                    class="card"
                >
                    <booking-summary
                        :event="booking.period.event"
                        :booking="booking"
                        :errors="[]"
                    >
                    </booking-summary>

                    <div class="card-body">
                        <div
                            v-if="loadingStatus"
                            class="d-flex align-items-center mb-3"
                        >
                            <strong v-if="inPosMode()">{{ $t('followInstructionsPayTerminal') }}</strong>
                            <strong v-else>{{ $t('loading') }}</strong>
                            <div class="spinner-border ml-auto" role="status" aria-hidden="true"></div>
                        </div>

                        <div v-if="!loadingStatus">
                            <div v-if="booking.status  === 'open'">
                                <p class="card-text">
                                    {{ $t('confirmation.open') }}
                                </p>

                                <p v-if="booking.period.assign_started">
                                    {{ $t('confirmation.openAssignStarted') }}
                                </p>

                                <p v-else class="card-text">
                                    <i18n path="confirmation.openAssignNotStarted">
                                        <em slot="assign">{{ booking.period.assign }}</em>
                                    </i18n>
                                </p>
                            </div>

                            <div v-if="booking.status  === 'paid'">
                                <h3>
                                    <img v-if="inPosMode()"
                                         src="../assets/img/check-2x.png"
                                         alt=""
                                         class="mr-1"
                                    >
                                    <img v-else
                                         src="../assets/img/check.png"
                                         srcset="../assets/img/check.png 1x, ../assets/img/check-2x.png 2x, ../assets/img/check-3x.png 3x"
                                         alt=""
                                         class="mr-1"
                                    >
                                    {{ $t('confirmation.thanks') }}
                                </h3>

                                <template v-if="inPosMode()">
                                    <p class="card-text">
                                        {{ $t('confirmation.printingTicket') }}
                                    </p>
                                </template>

                                <template v-else>
                                    <p v-if="hasAllLockers" class="card-text">
                                        {{ $t('confirmation.paidHasLockers') }}
                                    </p>

                                    <p v-else-if="booking.period.assign_started">
                                        {{ $t('confirmation.paidAssignStarted') }}
                                    </p>

                                    <p v-else class="card-text">
                                        <i18n path="confirmation.paidAssignNotStarted">
                                            <em slot="assign">{{ booking.period.assign }}</em>
                                        </i18n>
                                    </p>
                                </template>
                            </div>

                            <div v-if="booking.status === 'canceled'">
                                <h3>
                                    {{ $t('confirmation.canceled') }}
                                </h3>

                                <p class="card-text">
                                    {{ $t('confirmation.canceledText') }}
                                </p>
                            </div>

                            <template v-if="!inPosMode()">
                                <locker-code
                                    v-for="locker in lockers"
                                    v-bind:key="locker.locker_number"
                                    :lockerSize="locker.size"
                                    :locker="locker"
                                ></locker-code>
                            </template>
                        </div>

                        <router-link
                            v-if="!loadingStatus"
                            :to="'/event/' + booking.period.event.code"
                            class="btn btn-secondary btn-block mt-3"
                        >
                            {{ inPosMode() ? $t('backToHomeScreen') : $t('rentAnotherLocker') }}
                        </router-link>
                    </div>

                    <locker-company-logo></locker-company-logo>
                </div>

                <button
                    v-if="bookingError"
                    class="btn btn-secondary btn-block mt-4"
                    @click="reload"
                >
                    {{ $t('refresh') }}
                </button>
            </div>

            <div
                v-if="booking && hasAllLockers && inPosMode()"
                class="print-content"
            >
                <ticket
                    v-if="activeTicket"
                    v-bind:key="activeTicket.locker_number"
                    :lockerSize="activeTicket.size"
                    :locker="activeTicket"
                    :period="booking.period"
                    @loaded="ticketLoaded"
                ></ticket>
            </div>
        </div>
        <select-language v-if="booking !== null"/>
    </div>
</template>

<script>
    import BookingSummary from '../components/BookingSummary';
    import LockerCode from '../components/LockerCode';
    import LockerCompanyLogo from '../components/LockerCompanyLogo';
    import Ticket from '../components/Ticket';
    import eventBus from '../eventBus';
    import BookingService from '../services/BookingService';
    import posMixin from '../mixins/posMixin';
    import EventBus from '@/eventBus';
    import SelectLanguage from '../components/SelectLanguage.vue';
    import Booking from '@/models/Booking';

    const reloadTimoutPortal = 5; // Reload timeout for the portal in seconds
    const reloadTimoutPos = 1; // Reload timeout for the pos in seconds

    export default {
        components: {
            SelectLanguage,
            LockerCode,
            BookingSummary,
            LockerCompanyLogo,
            Ticket
        },

        mixins: [posMixin],

        props: [
            'token'
        ],

        data() {
            return {
                booking: null,
                bookingError: false,
                loadingStatus: true,
                waitingForStatus: false,
                printedTickets: 0
            }
        },

        computed: {
            /**
             * Get a list of all lockers.
             *
             * @return {Array}
             */
            lockers() {
                let lockers = [];

                for (let line of this.booking.lines) {
                    for (let locker of line.lockers) {
                        locker.size = line.name;

                        lockers.push(locker);
                    }
                }

                return lockers;
            },

            /**
             * Get the active ticket to print.
             *
             * @return {Object}
             */
            activeTicket() {
                return this.lockers[this.printedTickets];
            },

            /**
             * Is reload of booking needed?
             * - Status is open
             * - Status is paid and we're waiting for lockers
             */
            reloadBookingNeeded() {
                if (this.waitingForStatus && !this.inPosMode()) {
                    return false;
                }

                return this.booking.status === 'open' || (this.booking.status === 'paid' && this.waitingForLockers);
            },

            /**
             * Are we waiting for lockers?
             *
             * @return Boolean
             */
            waitingForLockers() {
                return this.booking.period.assign_started && !this.hasAllLockers;
            },

            /**
             * Get number of booked lockers.
             *
             * @return Number
             */
            numberOfBookedLockers() {
                let number = 0;

                for (let line of this.booking.lines) {
                    number += line.quantity;
                }

                return number;
            },

            /**
             * Get number of assigned lockers.
             *
             * @return Number
             */
            numberOfAssignedLockers() {
                let number = 0;

                for (let line of this.booking.lines) {
                    number += line.lockers.length;
                }

                return number;
            },

            /**
             * Has this booking assigned all lockers?
             *
             * @return Boolean
             */
            hasAllLockers() {
                return this.numberOfAssignedLockers === this.numberOfBookedLockers;
            },

            /**
             * Timeout in seconds for the current reload.
             *
             * @return Number
             */
            reloadTimout() {
                return this.inPosMode() ? reloadTimoutPos : reloadTimoutPortal;
            }
        },

        watch: {
            // Call again the method to get the event if the route changes
            '$route': 'init',

            /**
             * Check if all tickets are printed and redirect.
             *
             * @param {Number} val
             */
            printedTickets (val) {
                if (val === this.numberOfAssignedLockers) {
                    this.redirect();
                }
            }
        },

        mounted() {
            let that = this;

            /**
             * Increase printed tickets.
             */
            window.onafterprint = function () {
                that.printedTickets++;
            }
        },

        created() {
            this.init();
        },

        methods: {
            /**
             * Initialize booking confirmation component.
             */
            init() {
                this.getBooking();
            },

            /**
             * Get the requested booking.
             *
             * @return Promise
             */
            getBooking() {
                this.loadingStatus = true;

                return BookingService.get(this.token).then(response => {
                    if (!response.data) {
                        return;
                    }

                    this.booking = Object.assign(new Booking(), response.data);

                    // Update event for background
                    EventBus.$emit('setEvent', this.booking.period.event);

                    // If reload booking is needed, retrieve status/lockers again after x seconds
                    if (this.reloadBookingNeeded) {
                        this.reloadBooking();
                        return;
                    }

                    this.waitingForStatus = false;
                    this.loadingStatus = false;
                }).catch(error => {
                    this.bookingError = true;

                    if (error.response.status !== 404) {
                        return;
                    }

                    eventBus.$emit('error', this.$t('bookingNotFound'));
                });
            },

            /**
             * Reload booking.
             */
            reloadBooking() {
                this.waitingForStatus = true;

                setTimeout(function () {
                    this.getBooking()
                }.bind(this), this.reloadTimout * 1000);
            },

            /**
             * Called after a new ticket has been loaded.
             */
            ticketLoaded() {
                setTimeout(function () {
                    window.print();
                }, 100);
            },

            /**
             * Redirect back to home.
             */
            redirect() {
                this.$router.push({
                    'name': 'home'
                });
            },

            /**
             * Reload whole page.
             */
            reload() {
                window.location.reload();
            }
        }
    }
</script>
